import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  MagnifyingGlassIcon, PencilSquareIcon, ArrowsUpDownIcon, ArrowPathIcon, BanknotesIcon, ExclamationTriangleIcon,
  CreditCardIcon, ClockIcon, XMarkIcon, BuildingOffice2Icon,
} from '@heroicons/react/24/outline'
import { API_BASE_URL } from '../config/api'
import { fetchCsrfToken, clearCsrfToken } from '../lib/csrf'
import {
  getAdminOverview, getAdminWallets, getAdminTransactions, updateAdminSettings, updateWalletPriceOverride, adjustWallet,
  billingErrorMessage, type AdminBillingOverview, type AdminWallet, type AdminBillingTransaction, type TransactionType,
} from '../lib/billingApi'
import { formatMoney, formatMonthLabel, parseDollarsToCents, parseSignedDollarsToCents, centsToInputValue } from '../lib/money'
import { AdminShell } from '../components/admin/AdminShell'
import { Card, CardHeader, PillButton, EmptyState, Segmented, Skeleton, StatusTag, Modal, Avatar, SectionHead, cx } from '../components/dashboard/primitives'
import { FolderStatCard } from '../components/dashboard/FolderStatCard'
import { PageHead, HeaderStats } from '../components/dashboard/Layout'
import { StackedBarChart, Legend } from '../components/dashboard/charts'
import { TX_TYPE_LABEL, TxIcon, Pager, fmtDateTime } from '../components/billing/BillingSection'

const PAGE = 15
const TX_FILTERS: Array<{ value: '' | TransactionType; label: string }> = [
  { value: '', label: 'All' }, { value: 'topup', label: 'Top-ups' }, { value: 'verification', label: 'Verifications' },
  { value: 'applicant_fee', label: 'Applicant fees' }, { value: 'refund', label: 'Refunds' }, { value: 'adjustment', label: 'Adjustments' },
]
const usdAxis = (v: number) => (v >= 1000 ? `$${Math.round(v / 100) / 10}k` : `$${Math.round(v)}`)

function PriceModal({ title, sub, initialCents, allowClear, onClose, onSave }: {
  title: string; sub: string; initialCents: number | null; allowClear?: boolean
  onClose: () => void; onSave: (cents: number | null) => Promise<void>
}) {
  const [value, setValue] = useState(centsToInputValue(initialCents))
  const [busy, setBusy] = useState(false)
  const cents = parseDollarsToCents(value)
  const err = value && cents === null ? 'Enter a dollar amount, e.g. 1.00' : cents !== null && cents < 1 ? 'Price must be at least $0.01' : null
  const save = async (v: number | null) => {
    setBusy(true)
    try { await onSave(v); onClose() } catch (e) { toast.error(billingErrorMessage(e, 'Could not save')) } finally { setBusy(false) }
  }
  return (
    <Modal title={title} sub={sub} onClose={onClose} footer={<>
      {allowClear && initialCents !== null && <PillButton variant="outline" disabled={busy} onClick={() => save(null)}>Use default price</PillButton>}
      <PillButton variant="grey" onClick={onClose} disabled={busy}>Cancel</PillButton>
      <PillButton variant="primary" disabled={busy || cents === null || !!err} onClick={() => save(cents)}>{busy ? 'Saving…' : 'Save price'}</PillButton>
    </>}>
      <label className="sf-label" htmlFor="price-input">Price per verification (USD)</label>
      <div className="sf-input-wrap">
        <span className="sf-prefix">$</span>
        <input id="price-input" className={cx('sf-input', err && 'err')} inputMode="decimal" autoFocus value={value} onChange={e => setValue(e.target.value)} placeholder="1.00" />
      </div>
      {err ? <div className="sf-field-err">{err}</div> : <div className="sf-help">Charged to the organisation's wallet for each live verification.</div>}
    </Modal>
  )
}

function AdjustModal({ wallet, onClose, onDone }: { wallet: AdminWallet; onClose: () => void; onDone: () => void }) {
  const [amount, setAmount] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const cents = parseSignedDollarsToCents(amount)
  const amountErr = amount && cents === null ? 'Enter a non-zero amount, e.g. 25 or -10.50' : null
  const reasonErr = reason && reason.trim().length < 3 ? 'Give a short reason (3+ characters)' : null
  const submit = async () => {
    if (cents === null || reason.trim().length < 3) return
    setBusy(true)
    try {
      await adjustWallet(wallet.developer_id, cents, reason.trim())
      toast.success(`${cents > 0 ? 'Credited' : 'Debited'} ${formatMoney(Math.abs(cents))} ${cents > 0 ? 'to' : 'from'} ${wallet.name || wallet.email}`)
      onDone(); onClose()
    } catch (e) { toast.error(billingErrorMessage(e, 'Adjustment failed')) } finally { setBusy(false) }
  }
  return (
    <Modal title="Manual adjustment" sub={`${wallet.name || wallet.email} · current balance ${formatMoney(wallet.balance_cents)}`} onClose={onClose} footer={<>
      <PillButton variant="grey" onClick={onClose} disabled={busy}>Cancel</PillButton>
      <PillButton variant={cents !== null && cents < 0 ? 'dark' : 'primary'} disabled={busy || cents === null || reason.trim().length < 3} onClick={submit}>
        {busy ? 'Applying…' : cents === null ? 'Apply' : cents > 0 ? `Credit ${formatMoney(cents)}` : `Debit ${formatMoney(-cents)}`}
      </PillButton>
    </>}>
      <label className="sf-label" htmlFor="adj-amount">Amount (USD, signed)</label>
      <div className="sf-input-wrap">
        <span className="sf-prefix">$</span>
        <input id="adj-amount" className={cx('sf-input', amountErr && 'err')} inputMode="decimal" autoFocus value={amount} onChange={e => setAmount(e.target.value)} placeholder="25.00 or -10.00" />
      </div>
      {amountErr ? <div className="sf-field-err">{amountErr}</div> : <div className="sf-help">Positive credits the wallet, negative debits it.</div>}
      {cents !== null && (
        <div className="sf-card muted" style={{ padding: 14, borderRadius: 18, marginTop: 14, display: 'flex', justifyContent: 'space-between', fontSize: 13.5 }}>
          <span style={{ color: 'var(--sf-mid)' }}>New balance</span><b className="sf-num">{formatMoney(wallet.balance_cents + cents)}</b>
        </div>
      )}
      <div style={{ marginTop: 16 }}>
        <label className="sf-label" htmlFor="adj-reason">Reason (recorded on the ledger)</label>
        <textarea id="adj-reason" className={cx('sf-input', reasonErr && 'err')} rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Goodwill credit for outage on 12 Jun" maxLength={240} />
        {reasonErr && <div className="sf-field-err">{reasonErr}</div>}
      </div>
    </Modal>
  )
}

export function AdminBillingPage() {
  const navigate = useNavigate()
  const [authReady, setAuthReady] = useState(false)
  const [overview, setOverview] = useState<AdminBillingOverview | null>(null)
  const [overviewError, setOverviewError] = useState<string | null>(null)
  const [wallets, setWallets] = useState<{ items: AdminWallet[]; total: number } | null>(null)
  const [walletSearch, setWalletSearch] = useState('')
  const [walletQuery, setWalletQuery] = useState('')
  const [walletOffset, setWalletOffset] = useState(0)
  const [tx, setTx] = useState<{ items: AdminBillingTransaction[]; total: number } | null>(null)
  const [txType, setTxType] = useState<'' | TransactionType>('')
  const [txOffset, setTxOffset] = useState(0)
  const [txOrg, setTxOrg] = useState<AdminWallet | null>(null)
  const [editPrice, setEditPrice] = useState<'global' | AdminWallet | null>(null)
  const [adjusting, setAdjusting] = useState<AdminWallet | null>(null)
  const [refreshKey, setRefreshKey] = useState(0)

  // Platform-admin guard (same probe as the organisations page)
  useEffect(() => {
    fetch(`${API_BASE_URL}/api/admin/developers?limit=1`, { credentials: 'include' })
      .then(res => {
        if (res.ok) { setAuthReady(true); fetchCsrfToken(); return }
        clearCsrfToken()
        navigate(res.status === 401 ? '/admin/login' : '/admin/verifications')
      })
      .catch(() => { clearCsrfToken(); navigate('/admin/login') })
  }, [navigate])

  const refresh = useCallback(() => setRefreshKey(k => k + 1), [])

  useEffect(() => {
    if (!authReady) return
    getAdminOverview().then(o => { setOverview(o); setOverviewError(null) }).catch(e => setOverviewError(billingErrorMessage(e, 'Billing overview is unavailable')))
  }, [authReady, refreshKey])

  useEffect(() => {
    if (!authReady) return
    let c = false
    setWallets(null)
    getAdminWallets({ limit: PAGE, offset: walletOffset, search: walletQuery }).then(r => { if (!c) setWallets(r) }).catch(() => { if (!c) setWallets({ items: [], total: 0 }) })
    return () => { c = true }
  }, [authReady, walletOffset, walletQuery, refreshKey])

  useEffect(() => {
    if (!authReady) return
    let c = false
    getAdminTransactions({ limit: PAGE, offset: txOffset, type: txType, developer_id: txOrg?.developer_id }).then(r => { if (!c) setTx(r) }).catch(() => { if (!c) setTx({ items: [], total: 0 }) })
    return () => { c = true }
  }, [authReady, txOffset, txType, txOrg, refreshKey])

  const t = overview?.totals
  const monthly = overview?.monthly ?? []
  const hasMonthly = monthly.some(m => m.topups_cents || m.applicant_fees_cents || m.verification_charges_cents)
  const price = overview?.settings.price_per_verification_cents ?? null

  let body: JSX.Element
  if (!authReady || (!overview && !overviewError)) {
    body = <div className="sf-grid c4">{[0, 1, 2, 3].map(i => <Skeleton key={i} h={200} r={28} />)}</div>
  } else if (overviewError && !overview) {
    body = <Card><EmptyState icon={<ExclamationTriangleIcon />} title="Billing couldn't be loaded" action={<PillButton size="sm" variant="dark" icon={<ArrowPathIcon />} onClick={refresh}>Try again</PillButton>}>{overviewError}</EmptyState></Card>
  } else if (overview && !overview.enabled) {
    body = (
      <Card style={{ padding: 40 }}>
        <EmptyState icon={<CreditCardIcon />} title="Billing is switched off">
          Set <code>BILLING_ENABLED=true</code> (and the Flutterwave keys) on the backend to start charging organisations.
          Until then verifications are free and no wallets are debited.
        </EmptyState>
      </Card>
    )
  } else {
    body = (
      <>
        <div className="sf-grid c4">
          <FolderStatCard label="Revenue" tone="accent" value={formatMoney(t!.revenue_cents, { compact: true })} caption="Paying organisations" captionValue={t!.paying_orgs.toLocaleString()} />
          <FolderStatCard label="Top-ups" value={formatMoney(t!.topups_cents, { compact: true })} caption="Applicant fees" captionValue={formatMoney(t!.applicant_fees_cents)} />
          <FolderStatCard label="Charged" value={formatMoney(t!.verification_charges_cents, { compact: true })} caption="Verifications charged" captionValue={t!.verifications_charged.toLocaleString()} />
          <FolderStatCard label="Outstanding" value={formatMoney(t!.outstanding_wallet_balance_cents, { compact: true })} caption="Held in wallets" captionValue="Prepaid, not yet used" />
        </div>

        <div className="sf-grid main sf-section">
          <Card>
            <CardHeader title="Revenue by month" sub="Last 12 months · USD"
              action={<Legend items={[{ label: 'Top-ups', tone: 'accent' }, { label: 'Applicant fees', tone: 'ink' }, { label: 'Verification charges', tone: 'pale' }]} />} />
            {hasMonthly ? (
              <StackedBarChart
                labels={monthly.map(m => formatMonthLabel(m.month))}
                series={[
                  { key: 'topups', label: 'Top-ups', tone: 'accent', values: monthly.map(m => m.topups_cents / 100) },
                  { key: 'fees', label: 'Applicant fees', tone: 'ink', values: monthly.map(m => m.applicant_fees_cents / 100) },
                  { key: 'charges', label: 'Verification charges', tone: 'pale', values: monthly.map(m => m.verification_charges_cents / 100) },
                ]}
                formatValue={usdAxis}
                height={280}
                ariaLabel="Monthly revenue, stacked by source"
              />
            ) : <EmptyState icon={<BanknotesIcon />} title="No revenue yet">Bars appear after the first top-up or applicant payment.</EmptyState>}
          </Card>
          <Card>
            <CardHeader title="Top organisations" sub="By total spend" action={<PillButton variant="outline" size="sm" onClick={() => document.getElementById('wallets')?.scrollIntoView({ behavior: 'smooth' })}>See all</PillButton>} />
            {overview!.top_orgs.length ? (
              <div className="sf-list">
                {overview!.top_orgs.slice(0, 6).map(o => (
                  <div className="sf-row" key={o.developer_id}>
                    <Avatar name={o.name || o.email} size={40} />
                    <div className="sf-row-main">
                      <div className="sf-row-title">{o.name || o.email || o.developer_id.slice(0, 8)}</div>
                      <div className="sf-row-sub">Balance {formatMoney(o.balance_cents)}</div>
                    </div>
                    <div className="sf-row-end"><b style={{ color: 'var(--sf-ink)', fontSize: 14 }}>{formatMoney(o.spent_cents)}</b><br />spent</div>
                  </div>
                ))}
              </div>
            ) : <EmptyState icon={<BuildingOffice2Icon />} title="No paying organisations yet" />}
          </Card>
        </div>

        {/* wallets */}
        <div id="wallets" className="sf-section" style={{ marginTop: 34 }}>
          <SectionHead title="Wallets" count={wallets?.total} />
          <form className="sf-filter" onSubmit={e => { e.preventDefault(); setWalletOffset(0); setWalletQuery(walletSearch.trim()) }}>
            <div className="sf-filter-col" style={{ flex: 2 }}>
              <label htmlFor="wallet-search">Search organisations</label>
              <input id="wallet-search" placeholder="Name, email or company" value={walletSearch} onChange={e => setWalletSearch(e.target.value)} />
            </div>
            <div className="sf-filter-col">
              <label>Default price</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <b style={{ fontSize: 15 }}>{formatMoney(price)}</b>
                <PillButton size="xs" variant="grey" icon={<PencilSquareIcon />} onClick={() => setEditPrice('global')}>Edit</PillButton>
              </div>
            </div>
            <div className="sf-filter-col">
              <label>Results</label>
              <b style={{ fontSize: 15 }}>{walletQuery ? `Matching “${walletQuery}”` : 'All organisations'}</b>
            </div>
            <button type="submit" className="sf-filter-go" aria-label="Search wallets"><MagnifyingGlassIcon /></button>
          </form>
          <Card flush>
            {!wallets ? <div className="sf-card-pad"><Skeleton h={180} r={18} /></div> : wallets.items.length ? (
              <>
                <div className="sf-table-wrap">
                  <table className="sf-table">
                    <thead><tr><th>Organisation</th><th className="num">Balance</th><th>Price / check</th><th>Applicant fee</th><th>Updated</th><th style={{ textAlign: 'right' }}>Actions</th></tr></thead>
                    <tbody>
                      {wallets.items.map(w => (
                        <tr key={w.developer_id}>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <Avatar name={w.name || w.email} size={36} />
                              <div>
                                <div className="strong">{w.name || w.email || '—'}</div>
                                <div className="muted" style={{ fontSize: 12.5 }}>{[w.company, w.email].filter(Boolean).join(' · ')}</div>
                              </div>
                            </div>
                          </td>
                          <td className={cx('num strong', w.balance_cents < (w.price_override_cents ?? price ?? 0) && 'sf-amt-pos')}>{formatMoney(w.balance_cents)}</td>
                          <td>{w.price_override_cents !== null ? <span className="sf-chip accent">{formatMoney(w.price_override_cents)} · custom</span> : <span className="muted">{formatMoney(price)} · default</span>}</td>
                          <td>{w.applicant_fee_enabled ? <StatusTag tone="ok">On</StatusTag> : <StatusTag tone="neutral">Off</StatusTag>}</td>
                          <td className="muted">{w.updated_at ? fmtDateTime(w.updated_at) : '—'}</td>
                          <td>
                            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                              <PillButton size="xs" variant="grey" icon={<ClockIcon />} onClick={() => { setTxOrg(w); setTxOffset(0); document.getElementById('ledger')?.scrollIntoView({ behavior: 'smooth' }) }}>History</PillButton>
                              <PillButton size="xs" variant="grey" icon={<PencilSquareIcon />} onClick={() => setEditPrice(w)}>Price</PillButton>
                              <PillButton size="xs" variant="dark" icon={<ArrowsUpDownIcon />} onClick={() => setAdjusting(w)}>Adjust</PillButton>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pager offset={walletOffset} total={wallets.total} limit={PAGE} onChange={setWalletOffset} />
              </>
            ) : <EmptyState icon={<BuildingOffice2Icon />} title={walletQuery ? 'No organisations match' : 'No wallets yet'}>{walletQuery ? 'Try a different search.' : 'Wallets are created when organisations sign up.'}</EmptyState>}
          </Card>
        </div>

        {/* ledger */}
        <Card flush className="sf-section" id="ledger" style={{ marginTop: 26 }}>
          <div className="sf-card-pad" style={{ paddingBottom: 12 }}>
            <CardHeader title="All transactions" sub={txOrg ? `Showing ${txOrg.name || txOrg.email}` : 'Every wallet movement across organisations'}
              action={txOrg ? <PillButton size="sm" variant="outline" icon={<XMarkIcon />} onClick={() => { setTxOrg(null); setTxOffset(0) }}>Clear organisation</PillButton> : undefined} />
            <Segmented value={txType} options={TX_FILTERS} onChange={v => { setTxType(v); setTxOffset(0) }} ariaLabel="Transaction type" />
          </div>
          {!tx ? <div className="sf-card-pad"><Skeleton h={180} r={18} /></div> : tx.items.length ? (
            <>
              <div className="sf-table-wrap">
                <table className="sf-table">
                  <thead><tr><th>Transaction</th><th>Organisation</th><th>Date</th><th>Reference</th><th className="num">Amount</th><th className="num">Balance after</th></tr></thead>
                  <tbody>
                    {tx.items.map(r => (
                      <tr key={r.id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <TxIcon type={r.type} amount={r.amount_cents} />
                            <div>
                              <div className="strong">{TX_TYPE_LABEL[r.type] ?? r.type}</div>
                              <div className="muted" style={{ fontSize: 12.5, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.description || '—'}</div>
                            </div>
                          </div>
                        </td>
                        <td><div className="strong">{r.developer_name || '—'}</div><div className="muted" style={{ fontSize: 12.5 }}>{r.developer_email}</div></td>
                        <td className="muted">{fmtDateTime(r.created_at)}</td>
                        <td className="muted sf-num">{r.reference || '—'}</td>
                        <td className={cx('num', r.amount_cents >= 0 ? 'sf-amt-pos' : 'sf-amt-neg')}>{formatMoney(r.amount_cents, { signed: true })}</td>
                        <td className="num">{formatMoney(r.balance_after_cents)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pager offset={txOffset} total={tx.total} limit={PAGE} onChange={setTxOffset} />
            </>
          ) : <EmptyState icon={<BanknotesIcon />} title="No transactions">Nothing matches these filters yet.</EmptyState>}
        </Card>
      </>
    )
  }

  return (
    <AdminShell role="platform">
      <div className="sf-container">
        <PageHead
          title="Billing"
          sub="Revenue, organisation wallets and pricing across the platform."
          actions={<>
            {t && <HeaderStats items={[
              { label: 'Paying orgs', value: t.paying_orgs.toLocaleString() },
              { label: 'Charged checks', value: t.verifications_charged.toLocaleString() },
              { label: 'Per check', value: formatMoney(price) },
            ]} />}
            <PillButton variant="primary" icon={<PencilSquareIcon />} onClick={() => setEditPrice('global')} disabled={!overview?.enabled}>Set price</PillButton>
          </>}
        />
        <div className="sf-toolbar">
          <PillButton icon={<ArrowPathIcon />} onClick={refresh}>Refresh</PillButton>
          <PillButton icon={<BuildingOffice2Icon />} onClick={() => navigate('/admin/developers')}>Organisations</PillButton>
        </div>
        {body}
      </div>

      {editPrice === 'global' && (
        <PriceModal title="Default price per verification" sub="Applies to every organisation without a custom price." initialCents={price}
          onClose={() => setEditPrice(null)}
          onSave={async c => { if (c === null) return; await updateAdminSettings(c); toast.success(`Default price set to ${formatMoney(c)}`); refresh() }} />
      )}
      {editPrice && editPrice !== 'global' && (
        <PriceModal title="Custom price" sub={`${editPrice.name || editPrice.email} · default is ${formatMoney(price)}`} initialCents={editPrice.price_override_cents} allowClear
          onClose={() => setEditPrice(null)}
          onSave={async c => { await updateWalletPriceOverride(editPrice.developer_id, c); toast.success(c === null ? 'Using the default price' : `Custom price set to ${formatMoney(c)}`); refresh() }} />
      )}
      {adjusting && <AdjustModal wallet={adjusting} onClose={() => setAdjusting(null)} onDone={refresh} />}
    </AdminShell>
  )
}
